import { useState, useEffect, useCallback } from 'react'
import { retrieveLaunchParams } from '@telegram-apps/sdk-react'
import { authApi } from '@/domain/auth/authApi'
import { TelegramInitiateDTO } from '@/domain/auth/authTypes'
import { useDeviceInfosStore, useUserAuthSessionStore } from '@/store'
import { useTelegramAuthStore } from '@/store/useTelegramAuthStore'
import { detectTelegramWebView } from '@/utils/telegram'
import { telegramAuthLogger } from '@/services/telegramAuthLogger'

export interface TelegramUser {
  id: number
  first_name: string
  last_name?: string
  username?: string
  photo_url?: string
}

interface LaunchParams {
  initDataRaw?: string
  initData?: {
    user?: TelegramUser
  }
}

/** API error response structure */
interface ApiErrorResponse {
  response?: {
    status?: number
    data?: {
      detail?: {
        code?: string
        description?: string
      }
    }
  }
}

/**
 * Extracts telegram_id from initData string
 * Used when API returns 404 USER_NOT_FOUND (user not linked)
 */
function extractTelegramIdFromInitData(initDataRaw: string): string | null {
  try {
    const params = new URLSearchParams(initDataRaw)
    const userParam = params.get('user')
    if (userParam) {
      const user = JSON.parse(userParam)
      if (user.id) {
        return String(user.id)
      }
    }
    return null
  } catch (error) {
    return null
  }
}

/**
 * Gets initDataRaw from @telegram-apps/sdk launch parameters.
 * This is the RAW string needed for server-side HMAC validation.
 *
 * According to Telegram Mini App docs:
 * - retrieveLaunchParams().initDataRaw is the proper source
 * - The raw string must NOT be reconstructed from parsed object
 * - This preserves correct encoding and ordering for hash validation
 */
function getInitDataRaw(): string | null {
  try {
    const launchParams = retrieveLaunchParams() as LaunchParams

    // initDataRaw is the raw query string from tgWebAppData
    if (launchParams.initDataRaw) {
      return launchParams.initDataRaw
    }

    // Fallback: try to get from URL hash directly
    // tgWebAppData is in the hash fragment
    const hash = window.location.hash
    if (hash.includes('tgWebAppData=')) {
      // Extract the raw initData from URL
      const match = hash.match(/tgWebAppData=([^&]+)/)
      if (match) {
        return decodeURIComponent(match[1])
      }
    }

    return null
  } catch (error) {
    return null
  }
}

/**
 * Gets Telegram user info from launch params
 */
function getTelegramUser(): TelegramUser | null {
  try {
    const launchParams = retrieveLaunchParams() as LaunchParams
    if (launchParams.initData?.user) {
      return launchParams.initData.user
    }

    // Fallback to window.Telegram.WebApp
    if (window.Telegram?.WebApp?.initDataUnsafe?.user) {
      return window.Telegram.WebApp.initDataUnsafe.user
    }

    return null
  } catch {
    return null
  }
}

/**
 * Hook for Telegram WebApp authentication
 *
 * Provides:
 * - initiateTelegramAuth(): Calls /telegram/initiate with initData
 * - telegramUser: Current user info from Telegram
 * - isInTelegram: Whether running in Telegram environment
 * - authStatus: Current authentication status
 */
export const useTelegramAuth = () => {
  const [telegramUser, setTelegramUser] = useState<TelegramUser | null>(null)
  const [isReady, setIsReady] = useState(false)

  // Store access
  const deviceInfos = useDeviceInfosStore()
  const initializeSession = useUserAuthSessionStore((s) => s.initializeSession)

  const telegramId = useTelegramAuthStore((s) => s.telegramId)
  const authStatus = useTelegramAuthStore((s) => s.status)
  const setTelegramId = useTelegramAuthStore((s) => s.setTelegramId)
  const setStatus = useTelegramAuthStore((s) => s.setStatus)
  const setError = useTelegramAuthStore((s) => s.setError)
  const setInitDataRaw = useTelegramAuthStore((s) => s.setInitDataRaw)

  const isInTelegram = detectTelegramWebView()

  useEffect(() => {
    // Initialize on mount
    if (isInTelegram) {
      const user = getTelegramUser()
      if (user) {
        setTelegramUser(user)
      }
      setIsReady(true)
    }
  }, [isInTelegram])

  /**
   * Initiates Telegram authentication flow
   *
   * 1. Gets initDataRaw from launch parameters
   * 2. Calls /telegram/initiate API
   * 3. If tokens returned → initializes session (user is linked)
   * 4. If only telegram_id → stores it for later linking
   *
   * @returns TelegramInitiateDTO or null on error
   */
  const initiateTelegramAuth = useCallback(async (): Promise<TelegramInitiateDTO | null> => {
    if (!isInTelegram) {
      return null
    }

    setStatus('checking')
    setError(null)

    try {
      // Get raw initData from launch params
      const initDataRaw = getInitDataRaw()

      if (!initDataRaw) {
        throw new Error('initData is not available')
      }

      // Store and log initData
      setInitDataRaw(initDataRaw)
      telegramAuthLogger.logInitDataRaw(initDataRaw)

      // Prepare payload according to API spec
      const payload = {
        init_data: initDataRaw,
        device_token: deviceInfos.device_id || '',
        language: deviceInfos.language || 'en',
        platform: 'WEB' as const,
        app_version: deviceInfos.app_version || '1.0.0',
      }

      // Log the request
      telegramAuthLogger.logTelegramInitiate({
        initData: initDataRaw,
        deviceToken: payload.device_token,
        platform: payload.platform,
        language: payload.language,
        appVersion: payload.app_version,
      })

      // Call API
      const response = await authApi.telegramInitiate(payload)

      // Log the response
      telegramAuthLogger.logTelegramInitiateResponse({
        telegramId: response.telegram_id,
        hasAccessToken: !!response.access_token,
        hasRefreshToken: !!response.refresh_token,
      })

      // Store telegram_id for potential linking later
      setTelegramId(response.telegram_id)

      // Check if user is linked (has tokens)
      if (response.access_token && response.refresh_token) {
        // User is linked - initialize session
        await initializeSession({
          token: response.access_token,
          refreshToken: response.refresh_token,
        })
        setStatus('authenticated')
      } else {
        // User not linked - needs to link account
        setStatus('needs_linking')
      }

      return response
    } catch (error) {
      telegramAuthLogger.logTelegramInitiateError(error)

      // Check if this is a 404 USER_NOT_FOUND error (user not linked)
      const apiError = error as ApiErrorResponse
      const errorCode = apiError?.response?.data?.detail?.code
      const statusCode = apiError?.response?.status

      if (statusCode === 404 && errorCode === 'USER_NOT_FOUND') {
        // User not linked - extract telegram_id from initData
        const initDataRaw = getInitDataRaw()
        if (initDataRaw) {
          const extractedTelegramId = extractTelegramIdFromInitData(initDataRaw)
          if (extractedTelegramId) {
            setTelegramId(extractedTelegramId)
            setStatus('needs_linking')

            // Return partial response for linking flow
            return {
              telegram_id: extractedTelegramId,
              access_token: undefined,
              refresh_token: undefined,
            }
          }
        }
        // Could not extract telegram_id
        setError('Could not extract Telegram user ID')
        setStatus('error')
        return null
      }

      // Other errors
      setError(error instanceof Error ? error.message : 'Failed to initiate Telegram auth')
      setStatus('error')
      return null
    }
  }, [
    isInTelegram,
    deviceInfos,
    initializeSession,
    setTelegramId,
    setStatus,
    setError,
    setInitDataRaw,
  ])

  /**
   * Show haptic feedback notification
   */
  const showNotification = useCallback((type: 'error' | 'success' | 'warning') => {
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.notificationOccurred(type)
    }
  }, [])

  /**
   * Show haptic impact feedback
   */
  const showHapticFeedback = useCallback(
    (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' = 'light') => {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred(style)
      }
    },
    [],
  )

  /**
   * Close the Telegram Mini App
   */
  const closeApp = useCallback(() => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.close()
    }
  }, [])

  return {
    telegramUser,
    telegramId,
    isReady,
    isInTelegram,
    authStatus,
    initiateTelegramAuth,
    showNotification,
    showHapticFeedback,
    closeApp,
  }
}
